import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCycle } from '../context/CycleContext';
import CyclePaymentModal from './CyclePaymentModal';
import './CycleSelector.css';

const SECTION_LABELS = {
    'self-assessment': 'Self Assessment',
    'reflection': 'Reflection',
    'transformation': 'Transformation',
    'checkin-1': 'Check-in #01',
    'checkin-2': 'Check-in #02',
    'checkin-3': 'Check-in #03',
    'checkin-4': 'Check-in #04',
    'checkin-5': 'Check-in #05',
    'checkin-6': 'Check-in #06'
};

const REQUIRED_SECTIONS = Object.keys(SECTION_LABELS);

const CycleSelector = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentCycle, cycleHistory, startNewCycle, viewCycle, selectedCycleNumber, loading, paymentRequired } = useCycle();
    const [showModal, setShowModal] = useState(false);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState('');

    // Auto-open payment modal ONLY for brand-new users (zero cycles, not on callback page)
    useEffect(() => {
        if (!loading && paymentRequired && !currentCycle
            && !location.pathname.includes('/payment')) {
            setShowModal(true);
        }
    }, [loading, paymentRequired, currentCycle, location.pathname]);

    // ── Payment success handler ──
    const handlePaymentSuccess = async (paymentReference) => {
        setShowModal(false);
        setStarting(true);
        setError('');
        const result = await startNewCycle(paymentReference);
        setStarting(false);

        if (!result.success) {
            const remaining = result.remainingSections || [];
            if (remaining.length > 0) {
                const labels = remaining.map(s => SECTION_LABELS[s] || s).join(', ');
                setError(`${result.message} — Remaining: ${labels}`);
            } else {
                setError(result.message);
            }
        } else {
            if (location.pathname === '/journal/self-assessment') {
                window.location.reload();
            } else {
                navigate('/journal/self-assessment');
            }
        }
    };

    // ── Loading state ──
    if (loading) {
        return (
            <div className="cycle-selector">
                <p style={{ color: '#999', fontSize: '13px' }}>Loading…</p>
            </div>
        );
    }

    // ── STATE 1: No cycle at all (brand-new user) ──
    if (!currentCycle && paymentRequired) {
        return (
            <>
                <CyclePaymentModal
                    isOpen={showModal}
                    required={true}
                    onClose={() => {}}
                    onSuccess={handlePaymentSuccess}
                />
                <div className="cycle-selector">
                    <div className="cycle-selector-header">
                        <p style={{ color: '#666', fontSize: '13px', marginBottom: '10px' }}>
                            Complete payment to begin your health journal.
                        </p>
                        <button
                            className="cycle-new-btn"
                            onClick={() => setShowModal(true)}
                            disabled={starting}
                        >
                            {starting ? 'Starting...' : '🔒 Start Cycle 1 (Pay)'}
                        </button>
                    </div>
                    {error && <p className="cycle-error">{error}</p>}
                </div>
            </>
        );
    }

    // If somehow no cycle and no paymentRequired, nothing to show
    if (!currentCycle) return null;

    // ── STATE 2 & 3: Has cycle — show progress bar + optional Start New Cycle ──
    const completedCount = currentCycle.completedSections?.length || 0;
    const totalSections = REQUIRED_SECTIONS.length;
    const isComplete = completedCount >= totalSections;
    const progressPercent = Math.round((completedCount / totalSections) * 100);

    return (
        <>
            <CyclePaymentModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={handlePaymentSuccess}
            />
            <div className="cycle-selector">
                <div className="cycle-selector-header">
                    <div className="cycle-selector-info">
                        <h3>Cycle {currentCycle.cycleNumber} {isComplete ? '✅' : ''}</h3>
                        <p>{completedCount} of {totalSections} sections visited</p>
                        <div className="cycle-progress-bar">
                            <div
                                className={`cycle-progress-fill ${isComplete ? 'complete' : 'in-progress'}`}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                    </div>

                    {isComplete && (
                        <button
                            className="cycle-new-btn"
                            onClick={() => { setError(''); setShowModal(true); }}
                            disabled={starting}
                        >
                            {starting ? 'Starting...' : 'Start New Cycle'}
                        </button>
                    )}
                </div>

                {error && <p className="cycle-error">{error}</p>}

                {cycleHistory.length > 1 && (
                    <div className="cycle-history">
                        <p className="cycle-history-label">View Cycle:</p>
                        <select
                            className="cycle-dropdown"
                            value={selectedCycleNumber || currentCycle.cycleNumber}
                            onChange={(e) => viewCycle(parseInt(e.target.value))}
                        >
                            {cycleHistory.map(c => (
                                <option key={c.cycleNumber} value={c.cycleNumber}>
                                    Cycle {c.cycleNumber} {c.status === 'completed' ? '(Done)' : '(Active)'}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        </>
    );
};

export default CycleSelector;
